import { createReducer, createActions } from 'reduxsauce';
import uniqBy from 'lodash/uniqBy';
import get from 'lodash/get';

/* ------------------- Types and Action Creators -------------- */
const { Types, Creators } = createActions({
  createTicket: ['venueId'],
  reset: [],
  loadTicket: ['reset'],
  loadCurrentTicket: [],
  setTicket: ['ticket'],
  setTicketUsers: ['ticketUsers'],
  closeTicket: [],
  deleteTicket: [],
  loadPaymentMethods: [],
  addPaymentMethod: ['paymentMethod'],
  setCurrentMethod: ['currentMethod'],
  setPaymentMethods: ['paymentMethods'],
  makePayment: ['tips', 'portionCount', 'cvv'],
  setIsTable: ['isTable'],
  setPaying: ['paying'], // payment
  deleteCard: ['cardId'],
  setDefaultPaymentMethod: ['cardId'],
  setDeleting: ['deleting'],
  setLoadingTicket: ['loadingTicket'], // ticket
  applyPromotion: ['promotionId'],
  applyOfferDiscount: ['discountId'],

  setPayType: ['payType'],
  setSelectedItems: ['selectedItems'],
  updateSelectedItem: ['item', 'quantity'],
  setWatcherIds: ['watcherId'],
  setClearWatcherIds: [],

  setOptInProgram: ['programId'],
  deleteLoyaltyPoint: ['programId'],

  loadRewardLevels: [],
  setRewardLevels: ['rewardLevels'],

  setLoyaltyAutoOffers: ['loyaltyAutoOffers'],
  setLoyaltyAnnualOffers: ['loyaltyAnnualOffers'],

  loadLoyaltyPoints: ['isLoadJoinModal'],
  setLoyaltyPoints: ['loyaltyPoints'],
  setLoyaltyPrograms: ['loyaltyPrograms'],

  setLoyaltyTypeModal: ['loyaltyPopupTypeModal'],

  loadLoyaltyAutoOffers: ['programId'],

  setActivateOffer: ['offerId'],
  setDeactivateOffer: ['offerId'],

  setUpdateAutoOffer: ['offerId', 'isActive'],

  setRegistToken: ['pushToken'],
});

export const PayTypes = Types;

export default Creators;

/* ---------------- Selectors -------------- */
const selectPayType = (state) =>
  state.pay.ticket && state.pay.ticket.splitType
    ? state.pay.ticket.splitType
    : state.pay.payType;
const selectMyPortion = (state) => {
  const { ticket } = state.pay;
  if (!ticket || !ticket.checks) return 1;

  const userCount = ticket.users.length;
  const paidUsers = ticket.checks
    .filter((c) => c.status === 'paid')
    .map((c) => c.userId);
  const me = state.auth.user.id;

  if (userCount === paidUsers.length) return 0;

  const myPaid = paidUsers.find((p) => p.userId === me);

  if (myPaid) {
    return myPaid.portion / userCount;
  }

  return 1 / userCount;
};
export const PaySelectors = {
  selectTicket: (state) => state.pay.ticket,
  selectSelectedItems: (state) => state.pay.selectedItems,
  selectItems: (state) => {
    if (!state.pay.ticket || !state.pay.ticket.checks) return [];
    // if (!state.pay.ticket.paidUsers) return [];

    const quantityMap = {};
    const claimQuantityMap = {};
    const userMap = {};
    const paidUsers =
      state.pay.ticket &&
      state.pay.ticket.checks &&
      state.pay.ticket.checks.length > 0
        ? state.pay.ticket.checks
            .filter((c) => c.status === 'paid')
            .map((c) => c.userId)
        : [];
    paidUsers.forEach((u) => {
      if (typeof u.portion === 'object') {
        u.portion.forEach((p) => {
          quantityMap[p.id] = (quantityMap[p.id] || 0) + p.quantity;
          userMap[p.id] = userMap[p.id] || [];
          const user = state.pay.ticketUsers.find((t) => t.id === u.userId);
          if (user) {
            userMap[p.id].push(user);
          }
        });
      }
    });

    if (state.pay.ticket.claims) {
      Object.keys(state.pay.ticket.claims).forEach((uId) => {
        (state.pay.ticket.claims[uId] || []).forEach((p) => {
          userMap[p.id] = userMap[p.id] || [];
          const user = state.pay.ticketUsers.find((t) => t.id === uId);
          if (user) {
            userMap[p.id].push(user);
          }

          if (uId === state.auth.user.id) return;
          claimQuantityMap[p.id] = (claimQuantityMap[p.id] || 0) + p.quantity;
        });
      });
    }

    return state.pay.ticket.checks[0].items // Default Check
      .map((item) => {
        const paidQuantity = quantityMap[item.extId] || 0;
        const claimedQuantity = claimQuantityMap[item.extId] || 0;
        const remainingQuantity =
          item.quantity - paidQuantity - claimedQuantity;
        const selectedItem = state.pay.selectedItems.find(
          (i) => i.extId === item.extId
        );

        return {
          ...item,
          paidUsers: userMap[item.extId] || [],
          paidQuantity,
          remainingQuantity,
          claimedQuantity,
          selectedQuantity: selectedItem ? selectedItem.quantity : 0,
        };
      })
      .sort((a, b) => b.remainingQuantity - a.remainingQuantity);
  },
  selectTicketUsers: (state) => state.pay.ticketUsers,
  selectDeleting: (state) => state.pay.deleting,
  selectPaying: (state) => state.pay.paying,
  selectIsTable: (state) => state.pay.isTable,
  selectTicketUserCount: (state) => get(state, 'pay.ticket.userIds.length', 0),
  selectUnpaidUserCount: (state) => {
    const userCount = get(state, 'pay.ticket.userIds.length', 0);
    const paidUsers = get(state, 'pay.ticket.paidUsers', []);
    const paidPortions = paidUsers.reduce((total, p) => {
      return total + p.portion;
    }, 0);

    return userCount - paidPortions;
  },
  selectIsPaid: (state) => {
    const paidUsers = get(state, 'pay.ticket.paidUsers', []);
    const me = get(state, 'auth.user.id');
    return !!paidUsers.find((p) => p.userId === me);
  },
  selectMyPortion,
  selectPayType,
  selectMyPrice: (state) => {
    const { ticket } = state.pay;
    if (!ticket) return 0;

    const portion = selectMyPortion(state);
    const payType = selectPayType(state);

    if (payType === `portion`) {
      const unpaidUserCount = PaySelectors.selectUnpaidUserCount(state);

      if (unpaidUserCount === 1) {
        return (
          ticket.totals.due -
          ticket.totals.tax -
          ticket.totals.serviceCharges -
          ticket.totals.gratuity
        );
      }

      return ticket.totals.subTotal * portion;
    }

    return state.pay.selectedItems.reduce((sum, item) => sum + item.price, 0);
  },
  selectLoadingTicket: (state) => state.pay.loadingTicket,
  selectCurrentMethod: (state) => state.pay.currentMethod,
  selectPaymentMethods: (state) => state.pay.paymentMethods,
  selectActivePaymentMethod: (state) => {
    return state.pay.paymentMethods.find(
      (p) => p._id === state.pay.currentMethod
    );
  },
  selectWatcherIds: (state) => state.pay.watcherIds,
  selectRewardLevels: (state) => state.pay.rewardLevels,
  selectLoyaltyAutoOffers: (state) => state.pay.loyaltyAutoOffers,
  selectLoyaltyAnnualOffers: (state) => state.pay.loyaltyAnnualOffers,
  selectLoyaltyPoints: (state) => state.pay.loyaltyPoints,
  selectLoyaltyPrograms: (state) => state.pay.loyaltyPrograms,
  selectLoyaltyTypeModal: (state) => state.pay.loyaltyPopupTypeModal,
};

/* ---------------- Initial State -------------- */
export const INITIAL_STATE = {
  paymentMethods: [],
  ticketUsers: [],
  selectedItems: [],
  currentMethod: null,
  isTable: true,
  ticket: null,
  loadingTicket: false,
  deleting: false,
  paying: false,
  payType: 'item',
  watcherIds: [],
  rewardLevels: [],
  loyaltyPoints: [],
  loyaltyPrograms: [],
  loyaltyAutoOffers: [],
  loyaltyAnnualOffers: [],
  loyaltyPopupTypeModal: null,
};

/* ---------------- Reducers -------------- */
export const setTicket = (state, { ticket }) => ({
  ...state,
  ticket,
});

export const setPaymentMethods = (state, { paymentMethods }) => ({
  ...state,
  paymentMethods,
});

export const addPaymentMethod = (state, { paymentMethod }) => {
  if (!state.currentMethod) {
    return {
      ...state,
      paymentMethods: uniqBy([paymentMethod, ...state.paymentMethods], '_id'),
      currentMethod: paymentMethod._id,
    };
  }

  return {
    ...state,
    paymentMethods: uniqBy([paymentMethod, ...state.paymentMethods], '_id'),
    currentMethod: paymentMethod._id,
  };
};

export const setCurrentMethod = (state, { currentMethod }) => ({
  ...state,
  currentMethod,
});

export const setLoadingTicket = (state, { loadingTicket }) => ({
  ...state,
  loadingTicket,
});

export const setLoyaltyAutoOffers = (state, { loyaltyAutoOffers }) => ({
  ...state,
  loyaltyAutoOffers,
});

export const setUpdateAutoOffer = (state, { offerId, isActive }) => {
  const { loyaltyAutoOffers } = state;

  const updateLoyaltyOffers = loyaltyAutoOffers.map((item) => {
    let offerItem = item;

    if (offerItem.id === offerId) {
      offerItem = { ...offerItem, active: isActive };
    }

    return offerItem;
  });

  return {
    ...state,
    loyaltyAutoOffers: updateLoyaltyOffers,
  };
};

export const setLoyaltyAnnualOffers = (state, { loyaltyAnnualOffers }) => ({
  ...state,
  loyaltyAnnualOffers,
});

export const setRewardLevels = (state, { rewardLevels }) => ({
  ...state,
  rewardLevels,
});

export const setLoyaltyPoints = (state, { loyaltyPoints }) => ({
  ...state,
  loyaltyPoints,
});

export const setLoyaltyPrograms = (state, { loyaltyPrograms }) => ({
  ...state,
  loyaltyPrograms,
});

export const setLoyaltyTypeModal = (state, { loyaltyPopupTypeModal }) => ({
  ...state,
  loyaltyPopupTypeModal,
});

export const setPaying = (state, { paying }) => ({
  ...state,
  paying,
});

export const setDeleting = (state, { deleting }) => ({
  ...state,
  deleting,
});

export const setIsTable = (state, { isTable }) => ({
  ...state,
  isTable,
});

export const setTicketUsers = (state, { ticketUsers }) => ({
  ...state,
  ticketUsers,
});

export const setPayType = (state, { payType }) => ({ ...state, payType });
export const setSelectedItems = (state, { selectedItems }) => ({
  ...state,
  selectedItems,
});

export const setWatcherIds = (state, { watcherId }) => ({
  ...state,
  watcherIds: state.watcherIds.includes(watcherId)
    ? state.watcherIds
    : [...state.watcherIds, watcherId],
});

export const setClearWatcherIds = (state) => ({
  ...state,
  watcherIds: [],
});

export const reset = () => ({ ...INITIAL_STATE });

export const reducer = createReducer(INITIAL_STATE, {
  [Types.SET_TICKET]: setTicket,
  [Types.SET_PAYMENT_METHODS]: setPaymentMethods,
  [Types.ADD_PAYMENT_METHOD]: addPaymentMethod,
  [Types.SET_CURRENT_METHOD]: setCurrentMethod,
  [Types.SET_LOADING_TICKET]: setLoadingTicket,
  [Types.SET_REWARD_LEVELS]: setRewardLevels,
  [Types.SET_LOYALTY_AUTO_OFFERS]: setLoyaltyAutoOffers,
  [Types.SET_UPDATE_AUTO_OFFER]: setUpdateAutoOffer,
  [Types.SET_LOYALTY_ANNUAL_OFFERS]: setLoyaltyAnnualOffers,
  [Types.SET_LOYALTY_POINTS]: setLoyaltyPoints,
  [Types.SET_LOYALTY_PROGRAMS]: setLoyaltyPrograms,
  [Types.SET_LOYALTY_TYPE_MODAL]: setLoyaltyTypeModal,
  [Types.SET_PAYING]: setPaying,
  [Types.SET_DELETING]: setDeleting,
  [Types.SET_IS_TABLE]: setIsTable,
  [Types.SET_TICKET_USERS]: setTicketUsers,
  [Types.SET_SELECTED_ITEMS]: setSelectedItems,
  [Types.SET_WATCHER_IDS]: setWatcherIds,
  [Types.SET_CLEAR_WATCHER_IDS]: setClearWatcherIds,
  [Types.SET_PAY_TYPE]: setPayType,
  [Types.RESET]: reset,
});
